import type {
  IConfPersonasEmpresa,
  IConfPersonasPaciente
} from '@/modules/Administracion/interfaces'
import type { ICorreccionIdentificacion } from '@/modules/Administracion/interfaces/ICorreccionIdentificacion'
import type {
  IResponseApi,
  IResponseApiSearch
} from '@/modules/Core/interfaces/api/IApis'
import type { IConfPersonasEmpleado, tipoGeneralesEmpleado } from '@/modules/Administracion/interfaces/tablas/IConfPersonas'
import { BaseService } from '@/modules/Core/services/BaseService'
import type { AxiosError, AxiosResponse } from 'axios'
export class PersonaService extends BaseService<unknown> {
  constructor () {
    super({
      baseUrl: 'system/people/'
    })
  }
  async listPersona<T> (
    tipo: string,
    id: number
  ): Promise<AxiosResponse<IResponseApi<T[]>>> {
    return this.http.get<IResponseApi<T[]>>(`/list/${id}/${tipo}`)
  }

  async listEmpresa (): Promise<
    AxiosResponse<IResponseApi<IConfPersonasEmpresa[]>>
  > {
    return this.http.get<IResponseApi<IConfPersonasEmpresa[]>>(`/list/empresas`)
  }

  // Buscar elementos con parámetros
  async searchPersona<T> (
    tipo: string,
    params?: Record<string, unknown>
  ): Promise<AxiosResponse<IResponseApiSearch<T[]>>> {
    return this.http
      .get<IResponseApiSearch<T[]>>(`/search/${tipo}`, { params })
      .catch(this.errorHandle)
  }

  async listEmpleado (
    params: Record<string, string[]>
  ): Promise<AxiosResponse<IResponseApi<IConfPersonasPaciente[]>>> {
    return this.http
      .get<IResponseApi<IConfPersonasPaciente[]>>(`/list/empleados`, { params })
      .catch(this.errorHandle)
  }

  async searchPaciente (
    params: Record<string, string | string[]>
  ): Promise<AxiosResponse<IResponseApi<IConfPersonasPaciente[]>>> {
    return this.http
      .get<IResponseApi<IResponseApiSearch<IConfPersonasPaciente[]>>>(
        `/search-by-name-ident/pacientes`,
        { params }
      )
      .catch(this.errorHandle)
  }
  async searchEmpleado (
    params: Record<string, string | string[]>
  ): Promise<AxiosResponse<IResponseApi<IConfPersonasEmpleado[]>>> {
    return this.http
      .get<IResponseApi<IResponseApiSearch<IConfPersonasEmpleado[]>>>(
        `/search-by-name-ident/empleados`,
        { params }
      )
      .catch(this.errorHandle)
  }
  async searchPersonaByIdent<T> (
    tipo: string,
    params: Record<string, string | string[] | boolean>
  ): Promise<AxiosResponse<IResponseApi<T[]>>> {
    return this.http
      .get<IResponseApi<IResponseApiSearch<T[]>>>(
        `/search-by-name-ident/${tipo}`,
        { params }
      )
      .catch(this.errorHandle)
  }

  // Ver un elemento por ID
  async viewPersona<T> (
    tipo: string,
    id: number | string | string[]
  ): Promise<AxiosResponse<IResponseApi<T>>> {
    return this.http.get<T>(`/view/${id}/${tipo}`).catch(this.errorHandle)
  }

  // Crear un nuevo elemento
  async createPersona<T> (
    tipo: string,
    data: T
  ): Promise<AxiosResponse<IResponseApi<T>>> {
    return this.http.post<T>(`/create/${tipo}`, data).catch(this.errorHandle)
  }

  // Actualizar un elemento por ID
  async updatePersona<T> (
    tipo: string,
    id: number | string,
    data: Partial<T>
  ): Promise<AxiosResponse<IResponseApi<T>>> {
    return this.http
      .put<T>(`/update/${tipo}/${id}`, data)
      .catch(this.errorHandle)
  }

  // Eliminar un elemento por ID
  async deletePersona (
    tipo: string,
    id: number | string
  ): Promise<AxiosResponse<IResponseApi<void>>> {
    return this.http
      .delete<void>(`/delete/${tipo}/${id}`)
      .catch(this.errorHandle)
  }

  async getTiposPaciente<T> (): Promise<AxiosResponse<IResponseApi<T>>> {
    return this.http
      .get<IResponseApi<T>>(`/general-create-patient`)
      .catch(this.errorHandle)
  }
  async getTiposEmpleado<T> (): Promise<AxiosResponse<IResponseApi<T>>> {
    return this.http
      .get<IResponseApi<T>>(`/general-create-employee`)
      .catch(this.errorHandle)
  }
  async getTiposEmpresa<T> (): Promise<AxiosResponse<IResponseApi<T>>> {
    return this.http
      .get<IResponseApi<T>>(`/general-company`)
      .catch(this.errorHandle)
  }
  async getTiposEmpleadoDetalle (): Promise<
    AxiosResponse<IResponseApi<tipoGeneralesEmpleado>>
  > {
    return this.http
      .get<IResponseApi<tipoGeneralesEmpleado>>(`/general-employee-details`)
      .catch(this.errorHandle)
  }

  async deleteFirma (
    params: Record<string, unknown>
  ): Promise<AxiosResponse<IResponseApi<number>>> {
    return this.http.delete<IResponseApi<number>>(`/delete-signature`, {
      params
    })
  }
  async getFirma (
    params: Record<string, unknown>
  ): Promise<AxiosResponse<IResponseApi<string>>> {
    return this.http.get<IResponseApi<string>>(`/get-signature`, { params })
  }
  async desvincularUsuarioPaciente(
    copeId: number
  ): Promise<AxiosResponse<IResponseApi<Pick<IConfPersonasPaciente, 'userName'>>>> {
    try {
      const respuesta = await this.http.put<IResponseApi<Pick<IConfPersonasPaciente, 'userName'>>>(
        `/patient/unlink-user/${copeId}`
      )
      return respuesta
    } catch (error) {
      this.errorHandle(error as AxiosError)
      throw error
    }
  }

  // Corrección de documento del paciente (solo SUPERADMIN). `corregirSnapshots`
  // propaga el cambio a los datos congelados en las atenciones históricas.
  async fixIdentificacionPaciente (
    copeId: number,
    data: {
      copeTipoIdentificacion: number
      copeIdentificacion: string
      corregirSnapshots: boolean
    }
  ): Promise<AxiosResponse<IResponseApi<ICorreccionIdentificacion>>> {
    return this.http
      .put<IResponseApi<ICorreccionIdentificacion>>(
        `/patient/fix-identification/${copeId}`,
        data
      )
      .catch(this.errorHandle)
  }

  async fixIdentificacionEmpleado (
    copeId: number,
    data: {
      copeTipoIdentificacion: number
      copeIdentificacion: string
    }
  ): Promise<AxiosResponse<IResponseApi<ICorreccionIdentificacion>>> {
    return this.http
      .put<IResponseApi<ICorreccionIdentificacion>>(
        `/employee/fix-identification/${copeId}`,
        data
      )
      .catch(this.errorHandle)
  }

  async fixIdentificacionEmpresa (
    copeId: number,
    data: {
      copeTipoIdentificacion: number
      copeIdentificacion: string
    }
  ): Promise<AxiosResponse<IResponseApi<ICorreccionIdentificacion>>> {
    return this.http
      .put<IResponseApi<ICorreccionIdentificacion>>(
        `/company/fix-identification/${copeId}`,
        data
      )
      .catch(this.errorHandle)
  }

  async getServicePaciente (
    copeId: number,
    cocoId: number,
    tipoServicio?: string
  ): Promise<AxiosResponse<IResponseApi<IConfPersonasPaciente>>> {
    return this.http
      .get<IResponseApi<IConfPersonasPaciente>>(`/patient/${copeId}/contracts/${cocoId}/services-available/${tipoServicio || ''}`)
      .catch(this.errorHandle)
  }
}
